import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface DaySchedule {
  open: string;
  close: string;
  closed: boolean;
}

interface Schedule {
  monday?: DaySchedule;
  tuesday?: DaySchedule;
  wednesday?: DaySchedule;
  thursday?: DaySchedule;
  friday?: DaySchedule;
  saturday?: DaySchedule;
  sunday?: DaySchedule;
  emergency_available?: boolean;
}

interface ProviderScheduleCardProps {
  schedule: Schedule;
}

export default function ProviderScheduleCard({ schedule }: ProviderScheduleCardProps) {
  const { t } = useTranslation();

  const getCurrentStatus = () => {
    const now = new Date();
    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][
      now.getDay()
    ] as keyof Omit<Schedule, 'emergency_available'>;
    const todaySchedule = schedule[dayOfWeek];

    if (!todaySchedule || todaySchedule.closed) {
      return {
        isOpen: false,
        message: t('services.closedNow'),
        nextOpen: getNextOpening(),
      };
    }

    const currentTime = now.toTimeString().slice(0, 5);
    const isOpen = currentTime >= todaySchedule.open && currentTime <= todaySchedule.close;

    if (isOpen) {
      return {
        isOpen: true,
        message: t('services.openNow'),
        closesAt: todaySchedule.close,
      };
    }

    return {
      isOpen: false,
      message: t('services.closedNow'),
      nextOpen: getNextOpening(),
    };
  };

  const getNextOpening = () => {
    const now = new Date();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = now.getDay();

    for (let i = 1; i <= 7; i++) {
      const checkDay = (currentDay + i) % 7;
      const dayName = days[checkDay] as keyof Omit<Schedule, 'emergency_available'>;
      const daySchedule = schedule[dayName];

      if (daySchedule && !daySchedule.closed) {
        return {
          day: t(`services.registerProvider.${dayName}`),
          time: daySchedule.open,
        };
      }
    }

    return null;
  };

  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ] as const;

  const status = getCurrentStatus();

  return (
    <Card className="p-6 space-y-4 bg-card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Clock className="w-5 h-5" />
          {t('services.openingHours')}
        </h3>
        {schedule.emergency_available && (
          <Badge variant="destructive" className="gap-1">
            <AlertCircle className="w-3 h-3" />
            {t('services.emergencyService')}
          </Badge>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Badge
            variant={status.isOpen ? 'default' : 'secondary'}
            className={status.isOpen ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {status.message}
          </Badge>
          {status.isOpen && status.closesAt && (
            <span className="text-sm text-muted-foreground">
              • {t('services.closesAt')} {status.closesAt}
            </span>
          )}
          {!status.isOpen && status.nextOpen && (
            <span className="text-sm text-muted-foreground">
              • {t('services.opensAt')} {status.nextOpen.day} {status.nextOpen.time}
            </span>
          )}
        </div>
      </div>

      <div className="border-t border-border pt-4 space-y-2">
        {days.map((day) => {
          const daySchedule = schedule[day];
          if (!daySchedule) return null;

          return (
            <div key={day} className="flex items-center justify-between text-sm">
              <span className="font-medium capitalize text-foreground">
                {t(`services.registerProvider.${day}`)}
              </span>
              {daySchedule.closed ? (
                <span className="text-muted-foreground">
                  {t('services.closedAllDay')}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {daySchedule.open} - {daySchedule.close}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
